import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addCommentToNewsFeedPost,
  addNewsFeedPost,
  addReactionToNewsFeedPost,
  deleteNewsFeedPost,
  getOrgNewsFeedPosts,
  getProjectNewsFeedPosts,
  updateNewsFeedPost,
} from '../services/dataService';
import type { NewsFeedComment, NewsFeedPost } from '../types';
import { generateId } from '../utils/idGenerator';

interface NewsFeedDraft {
  content: string;
  photos: string[];
}

interface NewsFeedActor {
  id: string;
  name: string;
  avatar?: string;
}

interface UseNewsFeedOptions {
  organizationId?: string;
  projectId?: string;
  currentUser?: NewsFeedActor | null;
  fallbackPosts?: NewsFeedPost[];
}

const byLatestActivity = (left: NewsFeedPost, right: NewsFeedPost) =>
  new Date(right.updatedAt ?? right.createdAt).getTime() -
  new Date(left.updatedAt ?? left.createdAt).getTime();

function sortNewsFeedPosts(posts: NewsFeedPost[]): NewsFeedPost[] {
  return [...posts].sort(byLatestActivity);
}

export function useNewsFeed({
  organizationId,
  projectId,
  currentUser,
  fallbackPosts = [],
}: UseNewsFeedOptions) {
  const [remotePosts, setRemotePosts] = useState<NewsFeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [remoteStatus, setRemoteStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setRemotePosts([]);
      setLoading(false);
      setRemoteStatus('idle');
      return;
    }

    setLoading(true);

    try {
      const posts = projectId
        ? await getProjectNewsFeedPosts(organizationId, projectId)
        : await getOrgNewsFeedPosts(organizationId);

      setRemotePosts(sortNewsFeedPosts(posts));
      setRemoteStatus('success');
    } catch (error) {
      console.error('[useNewsFeed] Failed to fetch news feed:', error);
      setRemoteStatus('error');
    } finally {
      setLoading(false);
    }
  }, [organizationId, projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const posts = useMemo(() => {
    // Once a scoped remote feed loads successfully, treat it as source-of-truth.
    // This prevents stale fallback snapshots from resurfacing deleted posts.
    if (organizationId && remoteStatus === 'success') {
      return sortNewsFeedPosts(remotePosts);
    }

    const uniquePosts = new Map<string, NewsFeedPost>();

    sortNewsFeedPosts([...remotePosts, ...fallbackPosts]).forEach((post) => {
      if (!uniquePosts.has(post.id)) {
        uniquePosts.set(post.id, post);
      }
    });

    return sortNewsFeedPosts(Array.from(uniquePosts.values()));
  }, [fallbackPosts, organizationId, remotePosts, remoteStatus]);

  const createPost = useCallback(
    async (draft: NewsFeedDraft) => {
      if (!organizationId || !currentUser) {
        return;
      }

      const post: NewsFeedPost = {
        id: generateId('fp', projectId ?? organizationId),
        organizationId,
        projectId,
        scope: projectId ? 'project' : 'org',
        authorId: currentUser.id,
        authorName: currentUser.name,
        authorAvatar: currentUser.avatar,
        content: draft.content,
        photos: draft.photos.length > 0 ? draft.photos : undefined,
        createdAt: new Date().toISOString(),
        reactions: [],
        postComments: [],
        sourceType: 'manual',
      };

      await addNewsFeedPost(post);
      await refresh();
    },
    [currentUser, organizationId, projectId, refresh]
  );

  const editPost = useCallback(
    async (post: NewsFeedPost, content: string) => {
      if (!organizationId) {
        return;
      }

      await updateNewsFeedPost(organizationId, post.id, { content }, projectId ?? post.projectId);
      await refresh();
    },
    [organizationId, projectId, refresh]
  );

  const deletePost = useCallback(
    async (post: NewsFeedPost) => {
      if (!organizationId) {
        return;
      }

      await deleteNewsFeedPost(organizationId, post.id, projectId ?? post.projectId);
      await refresh();
    },
    [organizationId, projectId, refresh]
  );

  const commentOnPost = useCallback(
    async (post: NewsFeedPost, comment: NewsFeedComment) => {
      if (!organizationId) {
        return;
      }

      await addCommentToNewsFeedPost(organizationId, post.id, comment, projectId ?? post.projectId);
      await refresh();
    },
    [organizationId, projectId, refresh]
  );

  const reactToPost = useCallback(
    async (post: NewsFeedPost, emoji: string) => {
      if (!organizationId || !currentUser?.id) {
        return;
      }

      await addReactionToNewsFeedPost(
        organizationId,
        post.id,
        currentUser.id,
        emoji,
        projectId ?? post.projectId
      );
      await refresh();
    },
    [currentUser?.id, organizationId, projectId, refresh]
  );

  return {
    posts,
    loading,
    refresh,
    createPost,
    editPost,
    deletePost,
    commentOnPost,
    reactToPost,
  };
}